/**
 * Accessibility utilities for the STEWARD widget.
 *
 * The focus trap keeps Tab / Shift-Tab navigation cycling within a container
 * (typically a modal dialog rendered inside a Shadow DOM). On deactivation it
 * returns focus to the opener — the element the caller names at activation,
 * or else the one focused then — falling back to a caller-supplied element
 * when there was no opener or it can no longer take focus. A trap whose root
 * has left the document moves focus nowhere.
 */

/** A focusable selector list. `[tabindex="-1"]` is excluded — those nodes are
 *  programmatically focusable but not part of the Tab order. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** A focus trap binds Tab/Shift-Tab cycling to a root and remembers the
 *  previously-focused element so it can be restored on release. */
export interface FocusTrap {
  /** Save the opener and focus the first focusable inside the root. The
   *  opener is `opener` when one is passed — a caller that moves focus
   *  before activating names it, since by then it is no longer the active
   *  element — and otherwise the currently-focused element. `null` and the
   *  page's `<body>` (where focus rests when nothing holds it) both mean no
   *  opener. Idempotent — calling twice has no effect. */
  activate(opener?: Element | null): void;
  /** Release the trap and restore focus to the opener, or to
   *  options.fallbackFocus() when there was no opener or it cannot take
   *  focus (removed, disabled or hidden). When the root has left the
   *  document, focus is not moved. Idempotent. */
  deactivate(): void;
}

export interface FocusTrapOptions {
  /** Returns the element to focus on deactivation when there was no opener,
   *  or the opener cannot take focus: removed from the DOM (e.g. inside a
   *  closed shadow panel), disabled or hidden. */
  fallbackFocus?: () => HTMLElement | null;
  /** Where activate() should move focus.
   *  - `undefined` (default): focus the first focusable in the root.
   *  - `false`: leave focus where it is — caller manages initial focus.
   *  - function: call to get the element to focus (null returned ⇒ no move). */
  initialFocus?: false | (() => HTMLElement | null);
}

/** Read the active element across shadow boundaries — `document.activeElement`
 *  always reports the host element when focus is inside a shadow root, so we
 *  also walk into nested shadow roots to find the true innermost focused node. */
function deepActiveElement(): Element | null {
  let active: Element | null = document.activeElement;
  while (active && active.shadowRoot && active.shadowRoot.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

/** Visibility check — skip nodes hidden via `display:none`, `visibility:hidden`,
 *  or zero-size layout. `offsetParent` is null when an ancestor is
 *  `display:none` (except for `position:fixed` nodes, which we accept on the
 *  basis that they are usually visible and the cost of a false-positive is low). */
function isVisible(el: HTMLElement): boolean {
  if (el.hidden) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  // visibility:hidden keeps a non-zero rect, so offsetParent/size checks miss
  // it. Read it directly via getComputedStyle.
  const cs = el.ownerDocument?.defaultView?.getComputedStyle(el);
  if (cs && cs.visibility === 'hidden') return false;
  // offsetParent is null for `display:none` ancestors — but also for
  // `position:fixed` elements, so fall back to size check.
  if (el.offsetParent !== null) return true;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
}

/** Whether `node` is inside `root`, through any shadow roots nested under
 *  it. A control in a child component's own shadow tree (a composer's
 *  field, a message's copy button) is inside the trap, but `root.contains`
 *  stops at the child's shadow boundary and cannot see it. */
function composedContains(root: ShadowRoot | HTMLElement, node: Node | null): boolean {
  let at: Node | null = node;
  while (at) {
    if (root.contains(at)) return true;
    const owner = at.getRootNode();
    at = owner instanceof ShadowRoot ? owner.host : null;
  }
  return false;
}

function getFocusable(root: ShadowRoot | HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return nodes.filter(isVisible);
}

export function createFocusTrap(
  root: ShadowRoot | HTMLElement,
  options: FocusTrapOptions = {},
): FocusTrap {
  let active = false;
  let savedFocus: Element | null = null;

  // Capture-phase keydown so we see Tab events before focus naturally moves.
  // Listening on the root ensures shadow-DOM hosted controls are covered.
  const onKeydown = (e: Event): void => {
    if (!active) return;
    const ev = e as KeyboardEvent;
    if (ev.key !== 'Tab') return;

    const focusables = getFocusable(root);
    if (focusables.length === 0) {
      // Nothing to focus — prevent Tab from escaping the trap entirely.
      ev.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    // Use deepActiveElement so we get the true focused node inside the
    // shadow root, not the host element.
    const current = deepActiveElement();

    // Focus in a nested shadow tree is inside the trap, and Tab moves on
    // from it in document order; only focus that has truly left wraps.
    const inside = composedContains(root, current);
    if (ev.shiftKey) {
      if (current === first || !inside) {
        ev.preventDefault();
        last.focus();
      }
    } else {
      if (current === last || !inside) {
        ev.preventDefault();
        first.focus();
      }
    }
  };

  return {
    activate(opener?: Element | null): void {
      if (active) return;
      active = true;
      const from = opener === undefined ? deepActiveElement() : opener;
      // Focus on <body> is no focus at all: restoring it would leave the
      // keyboard user nowhere, so it is saved as no opener and deactivate
      // takes the fallback.
      savedFocus = from === document.body ? null : from;
      // Capture phase so we intercept Tab before the browser moves focus.
      root.addEventListener('keydown', onKeydown, true);
      if (options.initialFocus === false) return;
      if (typeof options.initialFocus === 'function') {
        const target = options.initialFocus();
        if (target) target.focus();
        return;
      }
      const focusables = getFocusable(root);
      if (focusables.length > 0) {
        focusables[0].focus();
      }
    },
    deactivate(): void {
      if (!active) return;
      active = false;
      root.removeEventListener('keydown', onKeydown, true);

      const saved = savedFocus as HTMLElement | null;
      savedFocus = null;
      // A root that has left the document was removed, not closed: the page has
      // moved on without it, so focus stays where the page put it.
      if (!root.isConnected) return;
      // Return to the opener only if focus lands there: focus() fails silently
      // on one that is gone, disabled or hidden, and those take the fallback.
      if (saved && typeof saved.focus === 'function') {
        saved.focus();
        if (deepActiveElement() === saved) return;
      }
      const fallback = options.fallbackFocus?.();
      if (fallback && typeof fallback.focus === 'function') {
        fallback.focus();
      }
    },
  };
}
